import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronUp,
  faEye,
} from "@fortawesome/free-solid-svg-icons";
import { DesignSize, CartItemDesignInstance } from "../models/CartItem";
import { useState } from "react";
import { sanitizeInput } from "../../../components/Input/IncrementNumberInput";
import { CasingOption } from "../../../shared/functions/casing";
import { Design } from "../../../../core/models/entities/Design";
import { DesignViewPopup } from "./DesignViewPopup";
import { SecondaryButton } from "../../../components/Buttons/SecondaryButton";
import Tooltip from "@mui/material/Tooltip";

export interface DesignInstanceViewModelProps {
  designInstance: CartItemDesignInstance;
  design: Design;
  casing?: CasingOption;
  index: number;
  updateLabel: (value: string) => void;
  handleInstanceSizeChange: (size: string) => void;
  handleInstanceQuantity: (quantity: number) => void;
  handleLogoText: (logoIndex: number, value: string | number) => void;
}

export function DesignInstanceComponent({
  index,
  casing,
  design,
  designInstance,
  updateLabel: handleOverall,
  handleInstanceSizeChange: handleSizeChange,
  handleInstanceQuantity: handleQuantity,
  handleLogoText: handleRowText,
}: DesignInstanceViewModelProps) {
  const [expanded, setExpanded] = useState<boolean>(true);
  const [popupOpen, setPopupOpen] = useState(false);
  const [highlightedLogoId, setHighlightedLogoId] = useState<string>("");

  const handleOpenPopup = (logoId: string) => {
    setHighlightedLogoId(logoId);
    setPopupOpen(true);
  };

  // Size options removed - now using input field for flexible size entry

  const designInstanceLogoRows = designInstance.logos.reduce<JSX.Element[]>(
    (rows, logo, logoIndex) => {
      if (logo.logoVariant === "static") {
        return rows;
      }

      rows.push(
        <div key={logoIndex} className={"text-xs flex space-x-1 p-1"}>
          <div className={"flex items-center"}>
            <p className={"w-[330px] pl-4 mr-1 font-semibold"}>
              {logo.locationIdentifier.toUpperCase()}{" "}
              <span className="italic font-normal">
                {logo.baseLogo?.title &&
                  `(${logo.baseLogo?.title}${
                    logo.baseLogo?.product?.sku &&
                    ` - ${logo.baseLogo?.product?.sku}`
                  })`}
              </span>
              {logo.designLogoInDesignId && (
                <Tooltip
                  title="See placement on design"
                  placement="top"
                  componentsProps={{
                    tooltip: {
                      sx: {
                        fontSize: "11px",
                      },
                    },
                  }}
                >
                  <span
                    className="cursor-pointer text-gray-500 hover:text-gray-700 ml-2"
                    onClick={() => handleOpenPopup(logo.designLogoInDesignId!)}
                  >
                    <FontAwesomeIcon icon={faEye} />
                  </span>
                </Tooltip>
              )}
            </p>
            <div className={"w-[94px]"}>
              {logo.logoVariant === "number" && (
                <input
                  className={"order-logo-design-input-child"}
                  placeholder={"Enter number..."}
                  type="number"
                  value={logo.value}
                  onKeyDown={sanitizeInput}
                  onChange={(e) => handleRowText(logoIndex, e.target.value)}
                />
              )}
              {logo.logoVariant === "name" && (
                <input
                  className={"order-logo-design-input-child"}
                  placeholder={"Enter name..."}
                  type="text"
                  value={logo.value}
                  onChange={(e) => {
                    // Allow only letters and common characters, disallow numbers
                    const filteredValue = e.target.value.replace(/[0-9]/g, "");
                    handleRowText(logoIndex, filteredValue);
                  }}
                />
              )}
            </div>
          </div>
        </div>,
      );

      return rows;
    },
    [],
  );

  const expand = (
    <>
      <div
        className={"h-4 w-4 flex items-center justify-center"}
        onClick={() => setExpanded((x) => !x)}
      >
        {expanded ? (
          <FontAwesomeIcon icon={faChevronUp} />
        ) : (
          <FontAwesomeIcon icon={faChevronDown} />
        )}
      </div>
    </>
  );

  const logoTypes = designInstance.logos.map((type) => type.logoVariant);

  return (
    <>
      <div id={"row"}>
        <div id={"logo-parent"} className={"text-xs flex justify-between p-1"}>
          <div className={"flex space-x-1"}>
            <div className={"min-w-[330px] w-[330px]"}>
              {logoTypes.includes("name") ? (
                <p className="order-logo-design-input">Design {index + 1}</p>
              ) : (
                <p className="order-logo-design-input">{index + 1}.</p>
              )}
            </div>
            <div className={"min-w-[90px] w-[90px]"}>
              <input
                className={"order-logo-design-input"}
                type="text"
                placeholder="Size..."
                value={designInstance.size || ""}
                maxLength={10}
                onChange={(e) => handleSizeChange(e.target.value)}
              />
            </div>
            <div className={"min-w-[90px] w-[90px] flex"}>
              <input
                className={"order-logo-design-input"}
                type={"number"}
                placeholder={"Quantity.."}
                value={designInstance.toDelivery}
                onKeyDown={sanitizeInput}
                onChange={(e) => handleQuantity(+e.target.value)}
              />
            </div>
          </div>
          <div className={"flex items-center"}>
            {designInstance.logos?.filter(
              (x) => x.logoVariant === "name" || x.logoVariant === "number",
            ).length > 0 && expand}
          </div>
        </div>
        {expanded && (
          <div id={"logo-children"} className={"w-full bg-white"}>
            {designInstanceLogoRows}
          </div>
        )}
      </div>
      <DesignViewPopup
        isOpen={popupOpen}
        onClose={() => setPopupOpen(false)}
        design={design}
        highlightLogoId={highlightedLogoId}
      />
    </>
  );
}
